import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { assistantApi, getAssistantSession } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { assistantUi } from "../i18n/uiText";

export default function AssistantWidget() {
  const { lang } = useLang();
  const A = assistantUi[lang] || assistantUi.ES;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const sessionRef = useRef(getAssistantSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: "assistant", content: A.greeting }]);
    // eslint-disable-next-line
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { reply } = await assistantApi.chat(text, sessionRef.current);
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: A.error }]);
    } finally {
      setLoading(false);
    }
  };

  const clean = (text) =>
    String(text)
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/(^|\n)\s*\*\s+/g, "$1• ")
      .replace(/(^|\n)\s*-\s+/g, "$1• ");

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[55] group flex items-center gap-2 rounded-full bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold pl-4 pr-5 py-3.5 shadow-2xl transition-all hover:scale-[1.03]"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="hidden sm:inline text-sm">{A.open}</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[55] w-[calc(100vw-2.5rem)] max-w-sm rounded-3xl bg-white shadow-2xl ring-1 ring-black/10 flex flex-col overflow-hidden animate-fade-up" style={{ height: "min(560px, 78vh)" }}>
          {/* Header */}
          <div className="bg-navy px-5 py-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-[var(--gold)] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#0A1C30]" />
            </span>
            <div className="flex-1">
              <div className="font-extrabold text-white leading-tight">{A.title}</div>
              <div className="text-xs text-white/60">{A.subtitle}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto supera-scroll px-4 py-4 space-y-3 bg-[#FAF8F1]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#0A1C30] text-white rounded-br-md"
                    : "bg-white text-[#2A3446] ring-1 ring-black/5 rounded-bl-md"
                }`}>
                  {m.role === "assistant" ? clean(m.content) : m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white ring-1 ring-black/5 rounded-2xl rounded-bl-md px-4 py-3">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-[#B9C0CC] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#B9C0CC] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 rounded-full bg-[#B9C0CC] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-3 border-t border-black/5 bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={A.placeholder}
              className="flex-1 h-11 px-4 rounded-full bg-[#F4F6F9] text-sm text-[#0A1C30] outline-none focus:ring-2 focus:ring-[var(--gold)] transition-shadow"
            />
            <button type="submit" disabled={loading || !input.trim()} className="w-11 h-11 flex-shrink-0 rounded-full bg-[var(--gold)] hover:bg-[var(--gold-soft)] disabled:opacity-50 flex items-center justify-center transition-colors">
              <Send className="w-5 h-5 text-[#0A1C30]" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
